import React, { useState, useRef, useEffect } from 'react';
import { Plus, X, Check } from 'lucide-react';

/**
 * Multi-select dropdown shown as orange chips. Pulls options from a flat
 * string[] and lets the user add brand-new ones via `onAddNew(value)`.
 *
 * Usage:
 *   <MultiSelectChips
 *     value={form.tenant_comments_list}
 *     onChange={list => setForm({...form, tenant_comments_list: list})}
 *     options={tenantCommentsOptions}
 *     placeholder="Pick tenant flags…"
 *     onAddNew={async v => { await api.post('/dropdowns/tenant_comments_options/add', { value: v }); refresh(); }}
 *   />
 */
export default function MultiSelectChips({
  value = [],
  onChange,
  options = [],
  placeholder = 'Select…',
  testId,
  onAddNew,
  emptyHint = 'No options selected.',
  className = '',
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef(null);

  useEffect(() => {
    const onDoc = e => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const selectedSet = new Set(value);
  const q = query.trim().toLowerCase();
  const filtered = options.filter(o => !q || o.toLowerCase().includes(q)).slice(0, 80);
  const trimmed = query.trim();
  const canAddNew = !!onAddNew && trimmed && !options.some(o => o.toLowerCase() === trimmed.toLowerCase());

  const toggle = (opt) => {
    if (selectedSet.has(opt)) onChange(value.filter(v => v !== opt));
    else onChange([...value, opt]);
  };

  const addNew = async () => {
    if (!trimmed || !onAddNew) return;
    await onAddNew(trimmed);
    onChange([...value, trimmed]);
    setQuery('');
  };

  return (
    <div ref={wrapRef} className={`relative ${className}`} data-testid={testId}>
      <div
        onClick={() => setOpen(true)}
        className={`min-h-[42px] w-full px-3 py-2 border rounded-lg text-sm cursor-text flex flex-wrap gap-1.5 items-center transition-colors ${open ? 'border-[#D35400] ring-2 ring-[#D35400]/10' : 'border-[#E2E2DF] hover:border-slate-300'}`}
      >
        {value.length === 0 && <span className="text-slate-400">{placeholder}</span>}
        {value.map(v => (
          <span key={v} className="inline-flex items-center gap-1 bg-[#FFF7ED] border border-[#D35400]/30 text-[#A04000] text-xs font-semibold rounded-full pl-2.5 pr-1 py-0.5">
            {v}
            <button
              type="button"
              data-testid={testId ? `${testId}-remove-${v}` : undefined}
              onClick={e => { e.stopPropagation(); toggle(v); }}
              className="ml-0.5 p-0.5 rounded-full hover:bg-[#D35400]/15"
              aria-label={`Remove ${v}`}
            >
              <X size={11} strokeWidth={2.5} />
            </button>
          </span>
        ))}
        <input
          type="text"
          value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          className="flex-1 min-w-[80px] bg-transparent outline-none text-sm placeholder:text-slate-400"
          placeholder={value.length ? '' : ''}
          data-testid={testId ? `${testId}-input` : undefined}
        />
      </div>

      {open && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-[#E2E2DF] rounded-lg shadow-lg max-h-64 overflow-y-auto">
          {filtered.length === 0 && !canAddNew && (
            <div className="px-3 py-2.5 text-xs text-slate-400 italic">No matches.</div>
          )}
          {filtered.map(opt => {
            const checked = selectedSet.has(opt);
            return (
              <button
                key={opt}
                type="button"
                onClick={() => toggle(opt)}
                data-testid={testId ? `${testId}-opt-${opt.replace(/\s+/g, '-').toLowerCase()}` : undefined}
                className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-sm text-left hover:bg-[#FFF7ED] ${checked ? 'bg-[#FFF7ED]/60' : ''}`}
              >
                <span className={checked ? 'font-semibold text-[#A04000]' : 'text-slate-700'}>{opt}</span>
                {checked && <Check size={14} className="text-[#D35400]" strokeWidth={2.5} />}
              </button>
            );
          })}
          {canAddNew && (
            <button
              type="button"
              onClick={addNew}
              data-testid={testId ? `${testId}-add-new` : undefined}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left text-[#D35400] hover:bg-[#FFF7ED] border-t border-[#E2E2DF] font-semibold"
            >
              <Plus size={14} strokeWidth={2.5} />
              Add "{trimmed}"
            </button>
          )}
        </div>
      )}
      {value.length === 0 && !open && emptyHint && (
        <p className="text-[11px] text-slate-400 italic mt-1">{emptyHint}</p>
      )}
    </div>
  );
}
